import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  BokkzIbDataService,
  IncomeTaxEntity,
  PGAOnderhoudsverplichtingen
} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {SelectFormControlItem, SharedUiFormControlsModule} from "@snelstart/ui-form-controls";
import {optionsLandcodesMetNLD,} from "../../gedeelde-componenten/gedeelde-componenten.component";
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  Subscription,
  switchMap,
  take,
  tap
} from "rxjs";
import {CommonModule} from "@angular/common";
import {SharedUiDetailsModule} from "@snelstart/ui-details";
import {SharedUiButtonModule} from "@snelstart/ui-button";
import {SharedUiDialogModule} from "@snelstart/ui-dialog";
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

type PGAOnderhoudsverplichtingenFormGroup = FormGroup<{
  periodiekBetaald: FormControl<number | null>;
  betaaldeAfkoopsom: FormControl<number | null>;
  verrekeningPensioenrechten: FormControl<number | null>;
  bijstandEx: FormControl<number | null>;
  overigeOnderhoudsverplichtingen: FormControl<number | null>;
  exPartner: FormGroup<{
    adresAlimentatieGerechtigde: FormGroup<{
      straat: FormControl<string | null>;
      huisNummer: FormControl<number | null>;
      huisNummerToevoeging: FormControl<string | null>;
      postcode: FormControl<string | null>;
      woonplaats: FormControl<string | null>;
      landCode: FormControl<string | null>;
    }>;
    gegevensAlimentatieGerechtigde: FormGroup<{
      roepnaam: FormControl<string | null>;
      voorletters: FormControl<string | null>;
      tussenvoegsels: FormControl<string | null>;
      achternaam: FormControl<string | null>;
      bsn: FormControl<string | null>;
      geboortedatum: FormControl<Date | null>;
      overlijdensdatum: FormControl<Date | null>;
      landCodeNationaliteit: FormControl<string | null>;
    }>;
  }>
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-feature-pga-onderhoudsverplichtingen',
  templateUrl: './pga-onderhoudsverplichtingen.component.html',
  styleUrls: ['./pga-onderhoudsverplichtingen.component.scss'],
  imports: [SharedUiDetailsModule, SharedUiFormControlsModule, SharedUiDialogModule, SharedUiButtonModule, CommonModule],
})
export class PgaOnderhoudsverplichtingenComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public index = 0;
  public dialogVisible = false;

  public formGroup = new FormGroup({
    pgaOnderhoudsverplichtingen: new FormArray<PGAOnderhoudsverplichtingenFormGroup>([]),
  });

  public optionsLandcodes: SelectFormControlItem<string>[] = optionsLandcodesMetNLD;

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> = this.dataService.incomeTaxEntity$.pipe(
    filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
    takeUntilDestroyed(this.destroyRef),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  public ngOnInit(): void {
    this.incomeTaxEntity$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((data) => {
      this.updateForm(data.aangifte?.persoonsgebondenAftrek?.pgaOnderhoudsverplichtingen || []);
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    const extractRelevant = () =>
      this.pgaOnderhoudsverplichtingen.getRawValue().map((w): PGAOnderhoudsverplichtingen => ({
        periodiekBetaald: w.periodiekBetaald,
        betaaldeAfkoopsom: w.betaaldeAfkoopsom,
        verrekeningPensioenrechten: w.verrekeningPensioenrechten,
        bijstandEx: w.bijstandEx,
        overigeOnderhoudsverplichtingen: w.overigeOnderhoudsverplichtingen,
        exPartner: w.exPartner
      }));

    this.changeSubscriptions.add(
      this.pgaOnderhoudsverplichtingen.valueChanges
        .pipe(
          debounceTime(500),
          tap(() => this.saveForm()),
          map(() => extractRelevant()),
          distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
          switchMap(() => this.dataService.calculateLatestIncomeTaxReturn()),
          takeUntilDestroyed(this.destroyRef),
        ).subscribe(),
    );
  }

  showDialog(index: number) {
    this.index = index;
    this.dialogVisible = true;
  }

  get pgaOnderhoudsverplichtingen(): FormArray {
    return this.formGroup.controls.pgaOnderhoudsverplichtingen as FormArray;
  }

  addRij(): void {
    this.pgaOnderhoudsverplichtingen.push(this.createFormGroup(), {emitEvent: false});
  }

  verwijderRij(index: number): void {
    this.pgaOnderhoudsverplichtingen.removeAt(index, {emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.formGroup.markAsDirty({emitEvent: false});
    this.saveForm();
  }

  createFormGroup(onderhoud?: PGAOnderhoudsverplichtingen) {
    return new FormGroup({
      periodiekBetaald: new FormControl<number | null>(onderhoud?.periodiekBetaald ?? null),
      betaaldeAfkoopsom: new FormControl<number | null>(onderhoud?.betaaldeAfkoopsom ?? null),
      verrekeningPensioenrechten: new FormControl<number | null>(onderhoud?.verrekeningPensioenrechten ?? null),
      bijstandEx: new FormControl<number | null>(onderhoud?.bijstandEx ?? null),
      overigeOnderhoudsverplichtingen: new FormControl<number | null>(onderhoud?.overigeOnderhoudsverplichtingen ?? null),
      exPartner: new FormGroup({
        adresAlimentatieGerechtigde: new FormGroup({
          straat: new FormControl<string | null>(onderhoud?.exPartner?.adresAlimentatieGerechtigde?.straat ?? null),
          huisNummer: new FormControl<number | null>(onderhoud?.exPartner?.adresAlimentatieGerechtigde?.huisNummer ?? null),
          huisNummerToevoeging: new FormControl<string | null>(
            onderhoud?.exPartner?.adresAlimentatieGerechtigde?.huisNummerToevoeging ?? null,
          ),
          postcode: new FormControl<string | null>(this.formatPostcode(onderhoud?.exPartner?.adresAlimentatieGerechtigde?.postcode) ?? null),
          woonplaats: new FormControl<string | null>(onderhoud?.exPartner?.adresAlimentatieGerechtigde?.woonplaats ?? null),
          landCode: new FormControl<string | null>(onderhoud?.exPartner?.adresAlimentatieGerechtigde?.landCode ?? 'NLD'),
        }),
        gegevensAlimentatieGerechtigde: new FormGroup({
          roepnaam: new FormControl<string | null>(onderhoud?.exPartner?.gegevensAlimentatieGerechtigde?.roepnaam ?? null),
          voorletters: new FormControl<string | null>(onderhoud?.exPartner?.gegevensAlimentatieGerechtigde?.voorletters ?? null),
          tussenvoegsels: new FormControl<string | null>(onderhoud?.exPartner?.gegevensAlimentatieGerechtigde?.tussenvoegsels ?? null),
          achternaam: new FormControl<string | null>(onderhoud?.exPartner?.gegevensAlimentatieGerechtigde?.achternaam ?? null),
          bsn: new FormControl<string | null>(onderhoud?.exPartner?.gegevensAlimentatieGerechtigde?.bsn ?? null),
          geboortedatum: new FormControl<Date | null>(onderhoud?.exPartner?.gegevensAlimentatieGerechtigde?.geboortedatum ?? null),
          overlijdensdatum: new FormControl<Date | null>(onderhoud?.exPartner?.gegevensAlimentatieGerechtigde?.overlijdensdatum ?? null),
          landCodeNationaliteit: new FormControl<string | null>(onderhoud?.exPartner?.gegevensAlimentatieGerechtigde?.landCodeNationaliteit ?? null),
        }),
      }),
    });
  }

  private updateForm(data: PGAOnderhoudsverplichtingen[]): void {
    const lijst = data || [];
    const formArray = this.pgaOnderhoudsverplichtingen;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1, {emitEvent: false});
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        const control = formArray.at(i) as FormGroup;
        control.patchValue(lijst[i], {emitEvent: false});
      } else {
        const formGroup = this.createFormGroup(lijst[i]);
        formArray.push(formGroup, {emitEvent: false});
      }
    }
  }

  saveForm(): void {
    const pgaOnderhoudsverplichtingen = this.formGroup.getRawValue().pgaOnderhoudsverplichtingen
      .filter((verplichtingen) =>
        verplichtingen.betaaldeAfkoopsom ||
        verplichtingen.periodiekBetaald ||
        verplichtingen.exPartner ||
        verplichtingen.overigeOnderhoudsverplichtingen ||
        verplichtingen.verrekeningPensioenrechten
      )
      .map((verplichtingen) => {
        const heeftBsn = verplichtingen.exPartner?.gegevensAlimentatieGerechtigde?.bsn ?? false;

        return {
          periodiekBetaald: verplichtingen.periodiekBetaald,
          betaaldeAfkoopsom: verplichtingen.betaaldeAfkoopsom,
          verrekeningPensioenrechten: verplichtingen.verrekeningPensioenrechten,
          bijstandEx: verplichtingen.bijstandEx,
          overigeOnderhoudsverplichtingen: verplichtingen.overigeOnderhoudsverplichtingen,
          exPartner: {
            adresAlimentatieGerechtigde: !heeftBsn ? {
              straat: verplichtingen.exPartner?.adresAlimentatieGerechtigde?.straat,
              huisNummer: verplichtingen.exPartner?.adresAlimentatieGerechtigde?.huisNummer,
              huisNummerToevoeging: verplichtingen.exPartner?.adresAlimentatieGerechtigde?.huisNummerToevoeging,
              postcode: verplichtingen.exPartner?.adresAlimentatieGerechtigde?.postcode,
              woonplaats: verplichtingen.exPartner?.adresAlimentatieGerechtigde?.woonplaats,
              landCode: verplichtingen.exPartner?.adresAlimentatieGerechtigde?.landCode,
            } : undefined,
            gegevensAlimentatieGerechtigde: {
              roepnaam: verplichtingen.exPartner?.gegevensAlimentatieGerechtigde?.roepnaam,
              voorletters: verplichtingen.exPartner?.gegevensAlimentatieGerechtigde?.voorletters,
              tussenvoegsels: verplichtingen.exPartner?.gegevensAlimentatieGerechtigde?.tussenvoegsels,
              achternaam: verplichtingen.exPartner?.gegevensAlimentatieGerechtigde?.achternaam,
              bsn: heeftBsn ? verplichtingen.exPartner?.gegevensAlimentatieGerechtigde?.bsn : undefined,
              geboortedatum: verplichtingen.exPartner?.gegevensAlimentatieGerechtigde?.geboortedatum,
              overlijdensdatum: verplichtingen.exPartner?.gegevensAlimentatieGerechtigde?.overlijdensdatum,
              landCodeNationaliteit: verplichtingen.exPartner?.gegevensAlimentatieGerechtigde?.landCodeNationaliteit,
            },
          }
        }
      });

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }

      return {
        ...currentEntity,
        aangifte: {
          ...currentEntity.aangifte,
          persoonsgebondenAftrek: {
            ...currentEntity.aangifte.persoonsgebondenAftrek,
            pgaOnderhoudsverplichtingen: pgaOnderhoudsverplichtingen,
          },
        },
      } as IncomeTaxEntity;
    });
  }

  formatPostcode(postcode: string | null | undefined): string {
    if (!postcode) return ''; // Retourneer een lege string als postcode null of undefined is

    // Verwijder alle niet-alfanumerieke tekens (bijv. spaties)
    const cleanedPostcode = postcode.replace(/[^a-zA-Z0-9]/g, '');

    // Zorg voor 4 cijfers en 2 hoofdletters
    if (cleanedPostcode.length >= 4) {
      const cijfers = cleanedPostcode.substring(0, 4).replace(/\D/g, ''); // Alleen cijfers
      const letters = cleanedPostcode
        .substring(4, 6)
        .replace(/[^a-zA-Z]/g, '')
        .toUpperCase(); // Alleen letters
      return `${cijfers}${letters}`;
    }

    return cleanedPostcode; // Als de postcode te kort is, retourneer de schoongemaakte versie
  }
}
